import React, { Component } from "react";
import { Container, Row, Col, Alert, ButtonGroup, Button } from "react-bootstrap";
import { GoX } from 'react-icons/go';
import Selector from "./selector/index";
import Workspace from "./workspace/index";
import Synchronizer from "./sync";
import SessionManager from "./session/manager";
import SessionContext from "./session/context";
import Navigation from "context/nav";

export default class PlaygroundShell extends Component {
  constructor(props) {
    super(props);

    this.state = {
      /**
       * State
       */

      // Global loading success status (manifest, credentials, etc)
      fatal: false,
      fatalMessage: null,
      // Definition operations progress flags
      progress: false,
      progressMessage: null,
      error: false,
      errorMessage: null,

      /**
       * Workflow
       */

      // All available workflow definitions
      workflowDefinitions: null,

      /**
       * Playground
       */

      // Playground manifest
      manifest: null,
      // All available playground definitions
      definitions: null,
      // Selected playground ID
      definitionId: null,
      // Selected playground definition item
      definition: null,
      // Initial state of currently selected definition object
      definitionObjectBackup: null,
      // Flag indicating whether definition is locally modified
      modified: false,

      /**
       * Workspace
       */

      nodes: [],
      edges: [],

      /**
       * UX
       */

      library: {
        visible: false,
      },
      details: {
        visible: false,
        view: null,
        data: null
      },
      wideContent: false,

      /**
       * Interfaces
       */

      // ReactFlow control interface
      workflowInterface: {
        setNodes: this.setNodes,
        setEdges: this.setEdges
      },
      // Shell control interface
      shellInterface: {
        canEditRevision: this.canEditRevision,
        savePlayground: this.savePlayground,
        revertPlayground: this.revertPlayground,
        markModified: this.markModified,
        selectSession: this.selectSession,
        sessionSelected: this.sessionSelected,
        getState: this.getState,
        updateState: this.updateState,
        updatePane: this.updatePane,
        updateProgress: this.updateProgress,
        updateDetails: this.updateDetails,
        getApp: this.getApp,
        getRevision: this.getRevision,
        getSync: this.getSync,
        createWorkflowPath: this.createWorkflowPath,
        enableSessionSync: this.enableSessionSync,
        switchDefinition: this.switchDefinition,
        reloadDefinitions: this.reloadDefinitions,
      },
    };

    this.sync = null;
  }

  /**
   * Shell interface
   */

  canEditRevision = () => {
    return this.props.app.api.canUpdateRevision(this.props.blob, this.props.revision);
  }

  savePlayground = () => {
    this.sync.savePlayground();
  }

  revertPlayground = () => {
    this.sync.revertPlayground();
  }

  markModified = () => {
    this.setState({ modified: true });
  }

  /**
   * Applies session selection based on URL upon initial page loading
   */
  selectSession = () => {
    // Skip if session is already selected
    if ( null != this.state.sessionId ) {
      return;
    }

    // Check the path
    const section = this.props.match.params.section;
    const sessionId = this.props.match.params.subSectionId;
    if ( "playground" != section || !sessionId ) {
      return
    }

    // Find session
    let requestedSession = null;
    if (this.sessionManager.sessions) {
      for ( const session of this.sessionManager.sessions ) {
        if ( session["id"] == sessionId ) {
          requestedSession = session;
          break;
        }
      }
    }
    
    if ( null == requestedSession ) {
      // Navigate to the root
      Navigation.push(this.props.history, this.createWorkflowPath("playground"));
      return;
    }

    // Select session
    this.sessionSelected(requestedSession);
  }

  sessionSelected = (session) => {
    // Update URL
    const sectionId = this.state.definition["alias"];
    const subSectionId = session["id"];
    Navigation.push(this.props.history, this.createWorkflowPath("playground", sectionId, subSectionId));

    // Disable sync if active
    if (this.sessionManager.sessionSync) {
      this.enableSessionSync(false);
    }

    // Begin the transition
    this.sessionManager.clearSessionState(session);
    return this.sessionManager.loadSessionContent()
      .then(() => {
        this.forceUpdate();
      });
  }

  updateState = (updates) => {
    const promise = new Promise((resolve, reject) => {
      this.setState(updates, () => {
        resolve();
      });
    });
    return promise;
  }

  getState = () => {
    return this.state;
  }

  updateProgress = (progress, progressMessage, error, errorMessage) => {
    this.setState({
      progress: progress,
      progressMessage: progressMessage,
      error: error,
      errorMessage: errorMessage
    });
  }

  updatePane = (pane, visible) => {
    let paneObject = this.state[pane];
    paneObject.visible = visible;
    this.setState({pane: paneObject});
  }

  updateDetails = (view, data) => {
    let details = this.state.details;
    if ( details.view == view && details.data == data ) {
      return;
    }

    details.view = view;
    details.data = data;
    this.setState({
      details: details
    });
  }

  getRevision = () => {
    return this.props.revision;
  }

  getApp = () => {
    return this.props.app;
  }

  getSync = () => {
    return this.sync;
  }

  createWorkflowPath = (section, sectionId, subSectionId) => {
    const orgId = this.props.match.params.orgId;
    const blobId = this.props.match.params.blobId;
    return Navigation.workflowPath(orgId, blobId, section, sectionId, subSectionId);
  }

  enableSessionSync = (enable) => {
    this.sessionManager.enableSessionSync(enable);
    this.forceUpdate();
  }

  switchDefinition = (definition) => {
    // Navigate to definition path
    const orgId = this.props.match.params.orgId;
    const blobId = this.props.match.params.blobId;
    const sectionId = definition["alias"];
    const subSectionId = this.state.sessionId;
    Navigation.push(this.props.history, this.createWorkflowPath("playground", sectionId, subSectionId));

    // Notify the view that new definition is selected and is about to be loaded
    this.setState({
      definition: definition,
      definitionId: definition["id"]
    }, () => {
      // Clear session state
      this.sessionManager.clearSessionState({});
      
      // Load definition itself
      this.sync.loadPlaygroundDefinition();
    });
  }

  reloadDefinitions = (reference) => {
    // Reload the list
    this.sync.loadPlaygroundDefinitions(reference);
  }

  /**
   * Workflow interface
   */

  setNodes = (nodes, isModified) => {
    this.setState({
      nodes: nodes
    });

    if ( isModified ) {
      this.markModified();
    }
  }

  setEdges = (edges, isModified) => {
    this.setState({
      edges: edges
    });

    if ( isModified ) {
      this.markModified();
    }
  }

  /**
   * UX
   */

  componentDidMount() {
    this.sessionManager = new SessionManager(
        this.props.app, this.props.revision, this.updateProgress, () => this.forceUpdate());
    this.sync = new Synchronizer(this.props.app, this.props.revision, this.state, this.sessionManager);
    this.sync.mounted = true;
    this.sessionManager.mounted = true;
    this.sync.loadPrerequisites();
    this.sessionManager.autoRefreshSessionEvents();
  }

  componentWillUnmount() {
    this.sync.mounted = false;
    this.sync = null;
    this.sessionManager.destroy();
    this.sessionManager = null;
  }

  render() {
    if ( this.state.fatal ) {
      return (
        <>
          <Container className="blob-header-row content-row">
            <Alert variant="warning">
              {this.state.fatalMessage}
            </Alert>
          </Container>
        </>
      );
    }

    const selectorVisible
      = !this.state.progress
      && null != this.state.manifest
      && null != this.state.definitions
      && null != this.sessionManager.sessions;

    const workspaceVisible
      = null != this.state.manifest
      && null != this.state.definition
      && null != this.state.definitionObjectBackup;

    return (
      <SessionContext.Provider value={this.sessionManager}>
        {this.state.progress &&
          <Container className="blob-header-row content-row">
            <Alert variant="primary">
              {this.state.progressMessage}
            </Alert>
          </Container>
        }

        {selectorVisible &&
          <Selector
            app={this.props.app}
            blob={this.props.blob}
            revision={this.props.revision}
            shell={this.state}
            sessionManager={this.sessionManager}
          />
        }

        {this.state.error &&
          <Container className="blob-header-row content-row">
            <Alert variant="warning">
              {this.state.errorMessage}
              <span className="float-right">
                <ButtonGroup size="sm" className="header-options">
                  <Button variant="light" className="btn-warning-close" onClick={(event) => {
                    this.updateProgress(false, null, false, null);
                  }}><GoX/></Button>
                </ButtonGroup>
              </span>
            </Alert>
          </Container>
        }

        {workspaceVisible &&
          <Workspace
            app={this.props.app}
            blob={this.props.blob}
            revision={this.props.revision}
            shell={this.state}
            sessionManager={this.sessionManager}
            shown={this.props.shown}
          />
        }
      </SessionContext.Provider>
    );
  }
}
